import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { isEmpty } from 'ramda'
import PropTypes from 'prop-types'
import ClearIcon from '@material-ui/icons/Clear'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  makeStyles,
  Drawer,
  AppBar,
  Tabs,
  Tab,
  IconButton,
  Toolbar,
  Typography,
} from '@material-ui/core'

import {
  AI_LOG_DISCARDED,
  AI_LOG_CREATED,
  AI_LOG_SUCCESS,
  AI_LOG_FAILED,
} from 'app/main/plansAndBilling/constants'
import { NORMAL, ERROR } from 'app/constants/statusColors'

import TabPanel from 'app/componentV4/Tabs/TabPanel'
import StatusChip from 'app/componentV4/Status/Chip'

import Loader from './Loader'
import { handleTitle, renderType, renderDetailActions } from './Commons'

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '85%',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '40%',
    },
  },
  appBar: {
    boxShadow: 'unset',
    borderBottom: `1px solid ${theme.palette.neutrals['200']}`,
    width: '100%',
    zIndex: 99,
    [theme.breakpoints.up('sm')]: {
      width: '85%',
    },
    [theme.breakpoints.up('md')]: {
      width: '60%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '40%',
    },
  },
  toolBar: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#000000',
    backgroundColor: '#FFFFFF',
    minHeight: 'unset',
    padding: '0 12px',
    height: 'fit-content !important',
    '& .MuiTab-root': {
      minWidth: 'fit-content',
      textTransform: 'capitalize',
    },
    '& .MuiTabs-scroller > span': {
      backgroundColor: '#000000',
      height: '1px',
    },
    '& .MuiTab-textColorInherit': {
      color: theme.colors['grey-600'],
      fontWeight: '600',
    },
    '& .MuiTab-textColorInherit.Mui-selected': {
      fontWeight: 'bold',
      color: '#183560',
    },
  },
}))

const ViewDetail = (props) => {
  const { isOpen = false, detail, isLoading = false, onClose } = props

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [color, setColor] = useState({ bg: '', credit: '' })

  const handleDetailPanel = useCallback((detail) => {
    switch (detail.status) {
      case AI_LOG_DISCARDED:
        return setColor({
          bg: 'bg-orange-50',
          credit: 'bg-orange-100 border-1 border-orange-200',
        })
      case AI_LOG_CREATED:
        return setColor({
          bg: 'bg-green-50',
          credit: 'bg-green-100 border-1 border-green-200',
        })
      default:
        return ''
    }
  }, [])

  const handleStatusType = (status) => {
    switch (status) {
      case AI_LOG_FAILED:
        return ERROR
      case AI_LOG_CREATED:
        return NORMAL
      default:
        return ''
    }
  }

  const handleStatusLable = (status) => {
    switch (status) {
      case AI_LOG_FAILED:
        return formatMessage({
          id: 'failed.label',
        })
      case AI_LOG_SUCCESS:
        return formatMessage({
          id: 'success.label',
        })
      default:
        return ''
    }
  }

  useEffect(() => {
    if (!isLoading && !isEmpty(detail)) {
      handleDetailPanel(detail)
    }
  }, [detail, handleDetailPanel, isLoading])

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      classes={{
        paper: classes.drawerPaper,
      }}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolBar}>
          <Tabs value={0} aria-label="Message Detail Tabs">
            <Tab
              label={formatMessage({
                id: 'aiUsageLog.detail.tab',
              })}
              className="text-blue-950"
              id="Message-Detail-Information"
              aria-controls="Message-Detail-Information"
            />
          </Tabs>
          <IconButton
            edge="end"
            aria-label="Close Message Detail"
            onClick={() => onClose()}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>
      {isLoading || isEmpty(detail) ? (
        <Loader />
      ) : (
        <div style={{ position: 'relative', top: '50px' }}>
          <TabPanel name="message-detail" value={0} index={0}>
            <div className="p-10 space-y-10">
              <div className={clsx(color.bg, 'rounded-4 p-5 space-y-10')}>
                {handleTitle(detail.title, detail.status, formatMessage)}
                <div className="flex justify-between">
                  <Typography className="text-16 text-grey-400">
                    {formatMessage(
                      {
                        id: 'last.updated.by.label',
                      },
                      {
                        fullName: `${detail?.createdBy?.firstName} ${detail?.createdBy?.lastName}`,
                      },
                    )}
                  </Typography>
                  <div
                    className={clsx(
                      color.credit,
                      'rounded-4 p-3 text-primary-500',
                    )}
                  >
                    {formatMessage(
                      {
                        id: 'credits.used.label',
                      },
                      {
                        credit: detail?.credits,
                      },
                    )}
                  </div>
                </div>
              </div>
              {detail?.data.map((list, index) => (
                <div
                  key={`aiCreditDetail-${index}`}
                  className="rounded-4 p-5 space-y-10 border-1 border-primary-350"
                >
                  <div className="flex justify-between">
                    <Typography className="text-16 text-grey-400">
                      {formatMessage({ id: renderType(list.type) })}
                      {renderDetailActions(
                        list.type,
                        list.actions,
                        formatMessage,
                      )}
                    </Typography>
                    <Typography className="text-14 text-grey-400 ml-10 whitespace-nowrap">
                      {moment(list.createdAt).format('MMM DD, YYYY LT')}
                    </Typography>
                  </div>
                  <div className="flex justify-between items-center">
                    <Typography className="text-14 text-grey-400">
                      {formatMessage(
                        {
                          id: 'last.updated.by.label',
                        },
                        {
                          fullName: `${list?.createdBy?.firstName} ${list?.createdBy?.lastName}`,
                        },
                      )}
                    </Typography>
                    <div className="flex space-x-3 items-center">
                      <Typography className="text-14 text-grey-400 capitalize">
                        <FormattedMessage
                          id="credits.label"
                          values={{
                            count: list.credits,
                            b: (text) => <b>{text}</b>,
                          }}
                        />{' '}
                      </Typography>
                      <StatusChip
                        type={handleStatusType(list.status)}
                        label={handleStatusLable(list.status)}
                      />
                    </div>
                  </div>
                </div>
              )) ?? null}
            </div>
          </TabPanel>
        </div>
      )}
    </Drawer>
  )
}

const dataPropTypes = {
  requestId: PropTypes.string,
  alertId: PropTypes.number,
  title: PropTypes.string,
  createdBy: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    deletedAt: PropTypes.string,
  }),
  records: PropTypes.number,
  credits: PropTypes.number,
  status: PropTypes.number,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      actions: PropTypes.shape({
        messageTone: PropTypes.string,
        contentLength: PropTypes.string,
        improveWriting: PropTypes.string,
        imageRatio: PropTypes.string,
        imageStyle: PropTypes.string,
      }),
      status: PropTypes.number,
      createdBy: PropTypes.shape({
        id: PropTypes.number,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        createdAt: PropTypes.string,
        deletedAt: PropTypes.string,
      }),
      credits: PropTypes.number,
      createdAt: PropTypes.string,
    }),
  ),
}

ViewDetail.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
  detail: PropTypes.shape(dataPropTypes).isRequired,
}

export default ViewDetail
